import { useState } from 'react';
import { Icon } from '../shared';
import { Button, Input } from '../ui';
import { authService } from '@foodbook/core-logic';

interface RegisterFormProps {
  onSwitchToLogin: () => void;
  onClose: () => void;
  onSuccess: (user: any) => void;
  onError?: (error: string) => void;
}

export const RegisterForm = ({
  onSwitchToLogin,
  onClose,
  onSuccess,
  onError,
}: RegisterFormProps) => {
  const [formData, setFormData] = useState({
    email: '',
    nickname: '',
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email) {
      onError?.('请输入邮箱地址');
      return;
    }
    if (!authService.validateEmail(formData.email)) {
      onError?.('请输入有效的邮箱地址');
      return;
    }
    if (!formData.password) {
      onError?.('请输入密码');
      return;
    }
    if (formData.password.length < 6) {
      onError?.('密码长度至少6位');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      onError?.('两次输入的密码不一致');
      return;
    }

    setIsLoading(true);
    try {
      const user = await authService.registerWithPassword(formData.email, formData.password, formData.nickname);
      onSuccess(user);
    } catch (err: any) {
      onError?.(err?.message || '注册失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center size-16 rounded-full bg-primary/10 mb-3">
          <Icon name="person_add" className="size-8 text-primary" />
        </div>
        <h2 className="text-xl font-black text-wood-darker dark:text-[#deb887]">创建账号</h2>
        <p className="text-sm text-wood-medium mt-1">使用邮箱和密码完成自托管注册</p>
      </div>

      <form onSubmit={handleRegister} className="space-y-4">
        <div>
          <label className="block text-sm font-bold text-wood-medium mb-1.5">
            邮箱地址 <span className="text-red-500">*</span>
          </label>
          <Input
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            placeholder="请输入您的邮箱"
            disabled={isLoading}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-wood-medium mb-1.5">
            昵称 <span className="text-wood-light text-xs font-normal">（可选）</span>
          </label>
          <Input
            type="text"
            value={formData.nickname}
            onChange={handleInputChange('nickname')}
            placeholder="给自己起个好听的名字"
            disabled={isLoading}
            maxLength={20}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-wood-medium mb-1.5">
            密码 <span className="text-red-500">*</span>
          </label>
          <Input
            type="password"
            value={formData.password}
            onChange={handleInputChange('password')}
            placeholder="请输入至少6位密码"
            disabled={isLoading}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-wood-medium mb-1.5">
            确认密码 <span className="text-red-500">*</span>
          </label>
          <Input
            type="password"
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            placeholder="请再次输入密码"
            disabled={isLoading}
            className="w-full"
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          className="w-full py-3 mt-2"
          size="lg"
          isLoading={isLoading}
          disabled={isLoading || !formData.email || !formData.password || !formData.confirmPassword}
        >
          {isLoading ? '注册中...' : '立即注册'}
        </Button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-wood-light/20"></div>
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-2 bg-white dark:bg-[#2c2a25] text-wood-light">已有账号？</span>
        </div>
      </div>

      <button
        type="button"
        onClick={onSwitchToLogin}
        className="w-full py-2.5 text-primary hover:text-primary/80 font-bold text-sm transition-colors border-2 border-primary/20 hover:border-primary/40 rounded-lg"
      >
        直接登录
      </button>

      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 p-2 text-wood-light hover:text-wood-dark transition-colors rounded-full hover:bg-wood-light/10"
      >
        <Icon name="close" className="size-5" />
      </button>
    </div>
  );
};
